/**
 * The sheet's head: what exercise this is, what the surface is for, and which column it is about.
 *
 * The mock's first row — caption, title, the column chips, the `?`. The chips are the sheet's one
 * navigation control: every column the MOVEMENT measures gets one (R9 — a rule on a column the
 * exercise does not measure is accepted at save, does nothing, and leaves the card promising a
 * progression forever), and pressing one changes which rule the sentence below is editing.
 *
 * ── The dot is never the only channel ───────────────────────────────────────────────────────
 * The mock colours each chip's dot by the family of the rule on that column, and a chip with no
 * rule takes the neutral ring. Colour alone would make "which of my columns progress" a question
 * only a sighted trainer can answer, so every chip's accessible name says it in words — the visible
 * label plus the state, which is the shape `accessible-name-must-contain-the-label` asks for.
 */

import { useRef } from 'react';

import { availableAxisFields, resolveGridColumns, type ExerciseDraft } from '~/entities/plan-draft';
import type { MeasurementFieldKey } from '~/shared/measurement';
import srOnly from '~/shared/ui/visuallyHidden.module.css';

import { useRovingRadioGroup } from '../lib/useRovingRadioGroup';
import type { Translate } from '../model/matrixCellState';
import { columnChipReading } from '../model/ruleStatement';

import styles from './ProgressionSheet.module.css';

const KEY = 'web:planBuilder.progression.sheet';

interface SheetHeadProps {
  readonly exercise: ExerciseDraft;
  readonly exerciseName: string;
  readonly field: MeasurementFieldKey;
  readonly onSelectColumn: (field: MeasurementFieldKey) => void;
  readonly showHints: boolean;
  readonly onToggleHints: () => void;
  readonly t: Translate;
}

export function SheetHead({
  exercise,
  exerciseName,
  field,
  onSelectColumn,
  showHints,
  onToggleHints,
  t,
}: SheetHeadProps) {
  /* The set table's own column vocabulary, in the set table's own order: the chips name the same
   * columns the trainer just typed into, so `planBuilder.day.field.*` is read rather than a second
   * label table written here. `showsRestColumn` is false, so every column names a measurement field. */
  const columns = resolveGridColumns(availableAxisFields(exercise.measurementFields), false);
  const fields = columns
    .map((column) => column.measurementField)
    .filter((measurementField): measurementField is MeasurementFieldKey => measurementField !== null);
  const labelKeys = Object.fromEntries(
    columns.flatMap((column) =>
      column.measurementField === null ? [] : [[column.measurementField, column.field.labelKey]],
    ),
  ) as Readonly<Record<MeasurementFieldKey, string>>;

  const columnsRef = useRef<HTMLDivElement>(null);
  /* ONE tab stop and arrows that move the choice. Three `aria-pressed` buttons were three stops for
   * a single choice, and „wciśnięty / niewciśnięty" is the wrong word for one of three columns. */
  const group = useRovingRadioGroup({
    groupRef: columnsRef,
    options: fields,
    value: field,
    onSelect: onSelectColumn,
  });

  return (
    <div className={styles.head}>
      <span className={styles.headTitles}>
        <span className={styles.caption}>{t(`${KEY}.caption`, { exercise: exerciseName })}</span>
        <span className={styles.title}>{t(`${KEY}.title`)}</span>
      </span>
      <span className={styles.spring} />
      <div
        ref={columnsRef}
        className={styles.columns}
        aria-label={t(`${KEY}.columnsLabel`)}
        {...group.groupProps}
      >
        {fields.map((measurementField) => {
          const chip = columnChipReading(exercise, measurementField);
          return (
            <button
              key={measurementField}
              type="button"
              className={styles.column}
              /* The mock's `colCards3`: the chosen chip is FILLED in the family's own hue when the
               * column carries a rule and in plain ink when it does not, and an unchosen chip that
               * carries one keeps the hue on its ink and its edge. */
              data-family={chip.mark ?? undefined}
              {...group.optionProps(measurementField)}
            >
              <span
                className={styles.columnDot}
                data-family={chip.mark ?? undefined}
                aria-hidden="true"
              />
              {t(labelKeys[measurementField] ?? '')}
              {/* The dot's meaning, for everyone who cannot see it. It trails the visible label
                * rather than replacing it, so the name a keyboard trainer hears CONTAINS the word
                * printed on the chip. */}
              <span className={srOnly.srOnly}>, {t(chip.stateKey)}</span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className={styles.help}
        aria-pressed={showHints}
        aria-label={t(`${KEY}.help.${showHints ? 'hide' : 'show'}`)}
        onClick={onToggleHints}
      >
        <span aria-hidden="true">?</span>
      </button>
    </div>
  );
}
